import * as THREE from 'three'
import type { DebugMaterialRegistry } from '../debug/debug-materials'

const textureLoader = new THREE.TextureLoader()

export function loadAuthenticTexture(name: string, repeatX = 1, repeatY = 1): THREE.Texture {
  const tex = textureLoader.load(`/assets/textures/${name}.png`)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.repeat.set(repeatX, repeatY)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.minFilter = THREE.NearestFilter
  tex.magFilter = THREE.NearestFilter
  return tex
}

export function createAuthenticWallMaterial(): THREE.MeshStandardMaterial {
  const map = typeof document !== 'undefined' ? loadAuthenticTexture('airpwall1', 2, 1) : null
  return new THREE.MeshStandardMaterial({
    map,
    color: 0xffffff,
    roughness: 0.5,
    metalness: 0.4,
  })
}

export function createAuthenticFloorMaterial(): THREE.MeshStandardMaterial {
  const map = typeof document !== 'undefined' ? loadAuthenticTexture('damwalkgrate', 8, 8) : null
  return new THREE.MeshStandardMaterial({
    map,
    color: 0xcccccc,
    roughness: 0.4,
    metalness: 0.6,
  })
}

export function createAuthenticCeilingMaterial(): THREE.MeshStandardMaterial {
  const map = typeof document !== 'undefined' ? loadAuthenticTexture('airwaffleceil3x', 6, 6) : null
  return new THREE.MeshStandardMaterial({
    map,
    color: 0x999999,
    roughness: 0.7,
    metalness: 0.3,
  })
}

export function createAuthenticDoorMaterial(): THREE.MeshStandardMaterial {
  const map = typeof document !== 'undefined' ? loadAuthenticTexture('chkdoorsec_h', 1, 1) : null
  return new THREE.MeshStandardMaterial({
    map,
    color: 0xffffff,
    roughness: 0.3,
    metalness: 0.7,
    polygonOffset: true,
    polygonOffsetFactor: -1.0,
    polygonOffsetUnits: -2.0,
  })
}

export function createAuthenticCrateMaterial(): THREE.MeshStandardMaterial {
  const map = typeof document !== 'undefined' ? loadAuthenticTexture('chkcrate-f_h', 1, 1) : null
  return new THREE.MeshStandardMaterial({
    map,
    color: 0xffffff,
    roughness: 0.5,
    metalness: 0.3,
  })
}

export function createAuthenticElevatorMaterial(): THREE.MeshStandardMaterial {
  const map = typeof document !== 'undefined' ? loadAuthenticTexture('baseelev1', 1, 1) : null
  return new THREE.MeshStandardMaterial({
    map,
    color: 0xffffff,
    roughness: 0.4,
    metalness: 0.6,
  })
}

export function createAuthenticForcefieldMaterial(): THREE.MeshStandardMaterial {
  const map = typeof document !== 'undefined' ? loadAuthenticTexture('elecfield1', 4, 4) : null
  if (map) {
    map.wrapS = THREE.RepeatWrapping
    map.wrapT = THREE.RepeatWrapping
  }

  const mat = new THREE.MeshStandardMaterial({
    map,
    color: 0x88ccff,
    emissive: 0x4488ff,
    emissiveMap: map,
    emissiveIntensity: 2.2,
    transparent: true,
    opacity: 0.92,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  })

  mat.onBeforeRender = () => {
    if (mat.map && typeof performance !== 'undefined') {
      const now = performance.now()
      mat.map.offset.x = (now * 0.0003) % 1
      mat.map.offset.y = (now * 0.00015) % 1
      mat.emissiveIntensity = 1.8 + Math.sin(now * 0.008) * 0.6
    }
  }

  return mat
}

const authenticMaterialCache = new Map<string, THREE.Material>()

/**
 * Get or create an authentic material derived purely from a manifest texture identifier.
 * Completely decouples rendering from view-level object type heuristics.
 */
export function getAuthenticMaterial(textureName?: string): THREE.Material {
  const isBrowser = typeof document !== 'undefined'
  if (!isBrowser) {
    return new THREE.MeshBasicMaterial()
  }

  const key = textureName || 'airpwall1'
  if (authenticMaterialCache.has(key)) {
    return authenticMaterialCache.get(key)!
  }

  let mat: THREE.Material
  if (key === 'elecfield1' || key.includes('field') || key.includes('elec')) {
    mat = createAuthenticForcefieldMaterial()
  } else if (key === 'chkdoorsec_h') {
    mat = createAuthenticDoorMaterial()
  } else if (key === 'chkcrate-f_h') {
    mat = createAuthenticCrateMaterial()
  } else if (key === 'baseelev1') {
    mat = createAuthenticElevatorMaterial()
  } else if (key === 'airpwall1') {
    mat = createAuthenticWallMaterial()
  } else {
    const map = loadAuthenticTexture(key, 1, 1)
    mat = new THREE.MeshStandardMaterial({
      map,
      color: 0xffffff,
      roughness: 0.4,
      metalness: 0.5,
      alphaTest: 0.2,
      transparent: true,
      side: THREE.DoubleSide,
    })
  }

  authenticMaterialCache.set(key, mat)
  return mat
}

/**
 * Cycles through authentic sector texture swatches on a targeted mesh.
 * Derives swatches directly from the mesh's authoritative metadata (populated from the manifest).
 */
export function cycleMeshSwatch(
  mesh: THREE.Mesh,
  _archetype?: string,
  debugRegistry?: DebugMaterialRegistry,
  step: number = 1,
): { texture: string; index: number; total: number } {
  const entry = debugRegistry?.getEntryByMesh(mesh)
  const currentTex = (mesh.userData?.currentTexture as string) || 'airpwall1'
  const swatches =
    entry?.meta?.availableSwatches && entry.meta.availableSwatches.length > 0
      ? entry.meta.availableSwatches
      : [currentTex]

  if (swatches.length === 0) return { texture: 'airpwall1', index: 0, total: 1 }

  let currentIdx = swatches.indexOf(currentTex)
  if (currentIdx === -1) currentIdx = 0

  const nextIdx = (currentIdx + step + swatches.length) % swatches.length
  const nextTex = swatches[nextIdx]

  const newMat = getAuthenticMaterial(nextTex)
  mesh.material = newMat
  if (!mesh.userData) mesh.userData = {}
  mesh.userData.currentTexture = nextTex
  mesh.userData.swatchIndex = nextIdx

  if (debugRegistry) {
    debugRegistry.updateOriginalMaterial(mesh, newMat)
  }

  return {
    texture: nextTex,
    index: nextIdx + 1,
    total: swatches.length,
  }
}

export function createTargetBullseyeTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 256
  const ctx = canvas.getContext('2d')!

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, 256, 256)

  // Concentric target rings
  const rings = [
    { r: 120, color: '#dd2222' },
    { r: 90, color: '#ffffff' },
    { r: 60, color: '#dd2222' },
    { r: 30, color: '#ffcc00' },
  ]
  for (const ring of rings) {
    ctx.fillStyle = ring.color
    ctx.beginPath()
    ctx.arc(128, 128, ring.r, 0, Math.PI * 2)
    ctx.fill()
  }

  const texture = new THREE.CanvasTexture(canvas)
  return texture
}
